import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deleteMailMessageResilient, isMutationTimeout, MAIL_MUTATION_DELETE_CHUNK, moveMailMessageResilient } from "@/lib/mail/mailMutationApi";
import { enqueueMailMutation } from "@/lib/mail/mailMutationSerial";
import { fetchMailEmlBatch } from "@/lib/api/storage";
import { storeFetchedEmlItems } from "@/lib/storage/storeFetchedEmlItems";
import { removeMessageBodyCacheEntry } from "@/lib/cache/messageBodyCache";
import { clearMailBodyDocumentCacheForMessage } from "@/lib/message/mailBodyDocumentCache";
import { markBodiesWarming, clearBodiesWarming } from "@/lib/mail/mailBodyWarmState";
import { fetchMessageBodyWithCache } from "@/lib/mail/fetchMessageBodyWithCache";
import { relocateStoredMessages } from "@/lib/storage/mailStorageRelocate";
import { relocateMessageBodyCache } from "@/lib/cache/messageBodyCache";
import { INBOX_ROW_EXIT_MS, pinInboxRowExitUids, unpinInboxRowExitUids } from "@/lib/mail/inboxRowExit";
import { markMailMessageSeen, markMailMessageUnread } from "@/lib/api/imap";
import { markFolderListViewedThisSession, patchFolderSnapshotRemoveUids, patchFolderSnapshotSeen } from "@/lib/mail/inboxFolderBus";
import { buildMessageContextMenuItems } from "@/lib/mail/mailMessageActions";
import {
  bulkDownloadAttachments,
  bulkSaveAttachmentsToCoffre,
  filterMessagesWithAttachments,
  type BulkAttachmentProgress,
  type MailAttachmentBulkTarget,
} from "@/lib/mail/bulkMailAttachments";
import { archiveMailMessagesToCoffre, type MailEmlArchiveTarget } from "@/lib/mail/archiveMailToCoffre";
import { prefetchCoffrePickerList, type CoffreListZone } from "@/lib/suite/coffreSuiteApi";
import { showMailToast } from "@/lib/ui/mailToast";
import {
  isMultiSelectClick,
  isRangeSelectClick,
  isTypingTarget,
  messageSelectionKey,
  pickNextMessageAfterRemove,
  selectUidRange,
  INBOX_MARK_READ_ON_OPEN_DELAY_MS,
} from "@/lib/mail/inboxSelection";
import type { MailFolderInvalidation } from "@/hooks/useMailRefresh";
import type { useMessageLabels } from "@/hooks/useMessageLabels";
import type { MailColorLabel } from "@/lib/mail/messageLabels";
import type { useMailSnooze } from "@/hooks/useMailSnooze";
import type { ComposeMode } from "@/lib/types/compose";
import type { MailAccountSummary, MailMessageSummary } from "@/lib/types/mail";
import { isGmailMailAccount } from "@/lib/accounts/googleOAuthAccounts";
import { unifiedMessageKey } from "@/lib/unified/unifiedInboxMerge";
import {
  beginMailMutation,
  failMailMutation,
  setMailMutationPhase,
  setMailMutationProgress,
  setMailMutationStepStatus,
} from "@/lib/mail/mailMutationProgress";
import { mailActivityMessageStepLabel } from "@/lib/mail/mailActivityJournalNote";
import {
  holdMutationUids,
  releaseMutationUids,
  releaseMutationHoldsOnly,
  tombstoneUids,
} from "@/lib/mail/mailMutationHold";

function messageRowKey(msg: Pick<MailMessageSummary, "uid" | "accountId">): string {
  return msg.accountId != null ? unifiedMessageKey(msg.accountId, msg.uid) : String(msg.uid);
}

type BulkMessageTarget = { uid: number; accountId?: number };

function parseBulkMessageKey(key: string): BulkMessageTarget {
  const sep = key.indexOf(":");
  if (sep >= 0) {
    const accountId = Number(key.slice(0, sep));
    const uid = Number(key.slice(sep + 1));
    return {
      uid: Number.isFinite(uid) && uid > 0 ? uid : 0,
      accountId: Number.isFinite(accountId) && accountId > 0 ? accountId : undefined,
    };
  }
  const uid = Number(key);
  return { uid: Number.isFinite(uid) && uid > 0 ? uid : 0 };
}

type InboxApi = {
  removeMessages: (
    uids: number[],
    options?: { accountResolver?: (uid: number) => number | undefined }
  ) => void;
  markMessageSeen: (uid: number, options?: { force?: boolean }) => void;
  markMessageUnread: (uid: number, options?: { force?: boolean }) => void;
  setMessagesSeen: (
    uids: number[],
    seen: boolean,
    options?: { force?: boolean; accountResolver?: (uid: number) => number | undefined }
  ) => void;
  markMessageFlagged: (uid: number, flagged: boolean) => void;
};

type Options = {
  account: MailAccountSummary | undefined;
  isOutboxView: boolean;
  selectedFolder: string;
  selectedUid: number | null;
  setSelectedUid: (uid: number | null) => void;
  displayMessages: MailMessageSummary[];
  labeledMessages: MailMessageSummary[];
  inboxMessages: MailMessageSummary[];
  inbox: InboxApi;
  messageLabels: ReturnType<typeof useMessageLabels>;
  mailSnooze: ReturnType<typeof useMailSnooze>;
  isSnoozedView?: boolean;
  isDraftsFolder: boolean;
  /** Dossier IMAP réel pour les actions (vue En attente). */
  resolveMessageFolder?: (uid: number, accountId?: number) => string;
  /** Liste IMAP active (false en vue virtuelle En attente / outbox). */
  inboxListActive?: boolean;
  adjustFolderUnread: (folder: string, delta: number) => void;
  adjustAccountFolderUnread?: (accountId: number, folder: string, delta: number) => void;
  invalidateFolders: (spec: MailFolderInvalidation) => void;
  recoverAfterMutationError: () => void;
  openDraftForEdit: (uid: number) => void;
  openComposeForUid: (uid: number, mode: ComposeMode) => Promise<void>;
  /** Réception unifiée — UIDs IMAP dupliqués entre comptes. */
  isUnifiedInboxView?: boolean;
  selectedReaderAccountId?: number | null;
  setSelectedReaderAccountId?: (id: number | null) => void;
  /** Dossier IMAP « En attente » — requis pour le move physique. */
  resolveSnoozeHoldFolder?: () => string | null;
  /** Corbeille Aurelia/ (ou override) pour delete. */
  resolveTrashFolder?: (accountId: number) => string | null;
  /** Pas de dossier branché → ouvrir les paramètres organisation. */
  onSnoozeFolderRequired?: () => void;
  /** Délai avant « lu » à l’ouverture. 0 = immédiat (lecteur mobile plein écran). */
  markReadOnOpenDelayMs?: number;
  /** Desktop split : ouvrir le mail suivant. Mobile : revenir à la liste. */
  advanceSelectionAfterDelete?: boolean;
};

export function useInboxBulkActions({
  account,
  isOutboxView,
  selectedFolder,
  selectedUid,
  setSelectedUid,
  displayMessages,
  labeledMessages,
  inboxMessages,
  inbox,
  messageLabels,
  mailSnooze,
  isSnoozedView = false,
  isDraftsFolder,
  resolveMessageFolder,
  inboxListActive = true,
  adjustFolderUnread,
  adjustAccountFolderUnread,
  invalidateFolders,
  recoverAfterMutationError,
  openDraftForEdit,
  openComposeForUid,
  isUnifiedInboxView = false,
  selectedReaderAccountId = null,
  setSelectedReaderAccountId,
  resolveSnoozeHoldFolder,
  resolveTrashFolder,
  onSnoozeFolderRequired,
  markReadOnOpenDelayMs = INBOX_MARK_READ_ON_OPEN_DELAY_MS,
  advanceSelectionAfterDelete = true,
}: Options) {
  const [checkedUids, setCheckedUids] = useState<Set<number>>(() => new Set());
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(() => new Set());
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    uid: number;
    accountId?: number;
  } | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [snoozeDialog, setSnoozeDialog] = useState<{ uid: number; subject?: string } | null>(
    null
  );
  const [snoozeBusy, setSnoozeBusy] = useState(false);
  const snoozeBusyRef = useRef(false);
  const [pendingActionCount, setPendingActionCount] = useState(1);
  const [exitingUids, setExitingUids] = useState<ReadonlySet<number>>(() => new Set());
  const [attachPicker, setAttachPicker] = useState<{
    mode: "coffre";
    targets: MailAttachmentBulkTarget[];
  } | null>(null);
  const [emlArchive, setEmlArchive] = useState<{ targets: MailEmlArchiveTarget[] } | null>(null);
  const [attachProgress, setAttachProgress] = useState<BulkAttachmentProgress | null>(null);
  const [attachBusy, setAttachBusy] = useState(false);
  const pendingMoveUidsRef = useRef<number[]>([]);
  const selectionAnchorRef = useRef<number | null>(null);
  const exitingUidsRef = useRef(new Set<number>());
  const markReadOnOpenTimerRef = useRef<number | null>(null);
  const markReadOnOpenKeyRef = useRef<string | null>(null);

  const cancelPendingMarkReadOnOpen = useCallback(() => {
    if (markReadOnOpenTimerRef.current != null) {
      window.clearTimeout(markReadOnOpenTimerRef.current);
      markReadOnOpenTimerRef.current = null;
    }
    markReadOnOpenKeyRef.current = null;
  }, []);

  useEffect(() => () => cancelPendingMarkReadOnOpen(), [cancelPendingMarkReadOnOpen]);

  const clearSelection = useCallback(() => {
    setCheckedUids(new Set());
    setCheckedKeys(new Set());
    setContextMenu(null);
    selectionAnchorRef.current = null;
  }, []);

  const resolveRowAccountId = useCallback(
    (uid: number, accountId?: number) => {
      if (accountId != null) return accountId;
      if (selectedReaderAccountId != null) {
        const match = displayMessages.find(
          (m) => m.uid === uid && m.accountId === selectedReaderAccountId
        );
        if (match?.accountId != null) return match.accountId;
      }
      if (contextMenu?.uid === uid && contextMenu.accountId != null) {
        return contextMenu.accountId;
      }
      const matches = displayMessages.filter((m) => m.uid === uid);
      if (matches.length === 1 && matches[0]?.accountId != null) {
        return matches[0].accountId;
      }
      return account?.id;
    },
    [account?.id, contextMenu, displayMessages, selectedReaderAccountId]
  );

  const findMessageByUid = useCallback(
    (uid: number, accountId?: number) => {
      if (accountId != null) {
        return (
          displayMessages.find((m) => m.uid === uid && m.accountId === accountId) ??
          labeledMessages.find((m) => m.uid === uid && m.accountId === accountId) ??
          inboxMessages.find((m) => m.uid === uid && m.accountId === accountId)
        );
      }
      return (
        displayMessages.find((m) => m.uid === uid) ??
        labeledMessages.find((m) => m.uid === uid) ??
        inboxMessages.find((m) => m.uid === uid)
      );
    },
    [displayMessages, inboxMessages, labeledMessages]
  );

  const messageImapFolder = useCallback(
    (uid: number, accountId?: number) =>
      resolveMessageFolder?.(uid, accountId) ??
      findMessageByUid(uid, accountId)?.imapFolder ??
      (isUnifiedInboxView ? "INBOX" : selectedFolder),
    [findMessageByUid, isUnifiedInboxView, resolveMessageFolder, selectedFolder]
  );

  /** Chemin unique lu/non lu — clic, clic droit et barre d’outils. */
  const applySeenToUids = useCallback(
    (uids: number[], seen: boolean, mode: "click" | "explicit") => {
      if (uids.length === 0) return;
      if (mode === "explicit") cancelPendingMarkReadOnOpen();

      const targets: number[] = [];

      for (const uid of uids) {
        if (uid <= 0) continue;
        const msg = findMessageByUid(uid);

        if (seen) {
          if (msg?.seen && mode === "click") continue;
          targets.push(uid);
          continue;
        }

        if (msg && !msg.seen && mode === "click") continue;
        targets.push(uid);
      }

      if (targets.length === 0) return;

      const unreadByFolder = new Map<string, number>();
      const unreadByAccountFolder = new Map<string, number>();
      const localUids: number[] = [];
      const apiByFolder = new Map<string, number[]>();

      for (const uid of targets) {
        const accountId = isUnifiedInboxView ? resolveRowAccountId(uid) : account?.id;
        const msg = findMessageByUid(uid, accountId);
        const fold = messageImapFolder(uid, accountId);
        // Vue En attente : UID liste = origine, UID IMAP hold = imapBodyUid.
        const actionUid =
          msg?.imapBodyUid != null && msg.imapBodyUid > 0 ? msg.imapBodyUid : uid;

        if (seen) {
          if (msg && !msg.seen) {
            unreadByFolder.set(fold, (unreadByFolder.get(fold) ?? 0) - 1);
            if (isUnifiedInboxView && accountId != null) {
              const key = `${accountId}\0${fold}`;
              unreadByAccountFolder.set(key, (unreadByAccountFolder.get(key) ?? 0) - 1);
            }
          }
        } else if (msg?.seen) {
          unreadByFolder.set(fold, (unreadByFolder.get(fold) ?? 0) + 1);
          if (isUnifiedInboxView && accountId != null) {
            const key = `${accountId}\0${fold}`;
            unreadByAccountFolder.set(key, (unreadByAccountFolder.get(key) ?? 0) + 1);
          }
        }

        if (inboxListActive && (!isUnifiedInboxView ? fold === selectedFolder : true)) {
          localUids.push(uid);
        } else {
          const listKey = isUnifiedInboxView && accountId != null ? `${accountId}\0${fold}` : fold;
          const list = apiByFolder.get(listKey) ?? [];
          list.push(actionUid);
          apiByFolder.set(listKey, list);
        }
      }

      if (isUnifiedInboxView && adjustAccountFolderUnread) {
        for (const [key, delta] of unreadByAccountFolder) {
          if (delta === 0) continue;
          const [acc, fold] = key.split("\0");
          adjustAccountFolderUnread(Number(acc), fold ?? "INBOX", delta);
        }
      } else if (!isUnifiedInboxView) {
        for (const [fold, delta] of unreadByFolder) {
          if (delta !== 0) adjustFolderUnread(fold, delta);
        }
      }

      if (localUids.length > 0) {
        inbox.setMessagesSeen(localUids, seen, {
          force: mode === "explicit",
          ...(isUnifiedInboxView ? { accountResolver: resolveRowAccountId } : {}),
        });
      }

      for (const [key, folderUids] of apiByFolder) {
        let accountId = account?.id;
        let fold = key;
        if (isUnifiedInboxView) {
          const [acc, folderName] = key.split("\0");
          accountId = Number(acc);
          fold = folderName ?? "INBOX";
        }
        if (accountId == null) continue;
        // Vue En attente / hors liste : pas de setMessagesSeen local — patch snapshot
        // pour que la liste snooze (et badges) reflètent le clic tout de suite.
        markFolderListViewedThisSession(accountId, fold);
        patchFolderSnapshotSeen(accountId, fold, folderUids, seen);
        const apiCall = seen
          ? markMailMessageSeen({ accountId, folder: fold, uids: folderUids })
          : markMailMessageUnread({ accountId, folder: fold, uids: folderUids });
        void apiCall.then((res) => {
          if (res.ok) return;
          patchFolderSnapshotSeen(accountId, fold, folderUids, !seen);
        });
      }
    },
    [
      account,
      adjustAccountFolderUnread,
      adjustFolderUnread,
      cancelPendingMarkReadOnOpen,
      findMessageByUid,
      inbox,
      inboxListActive,
      isUnifiedInboxView,
      messageImapFolder,
      resolveRowAccountId,
      selectedFolder,
    ]
  );

  const selectableInboxRows = useCallback(
    () =>
      displayMessages.filter((m) => m.uid > 0 && m.sendStatus == null),
    [displayMessages]
  );

  const selectableInboxUids = useCallback(
    () => selectableInboxRows().map((m) => m.uid),
    [selectableInboxRows]
  );

  const applyRangeSelection = useCallback(
    (fromUid: number, toUid: number, accountId?: number) => {
      if (isUnifiedInboxView) {
        const rows = selectableInboxRows();
        const fromKey = messageRowKey(
          rows.find((m) => m.uid === fromUid && (accountId == null || m.accountId === accountId)) ??
            rows.find((m) => m.uid === fromUid) ??
            { uid: fromUid, accountId }
        );
        const toKey = messageRowKey(
          rows.find((m) => m.uid === toUid && (accountId == null || m.accountId === accountId)) ??
            rows.find((m) => m.uid === toUid) ??
            { uid: toUid, accountId }
        );
        const keys = rows.map((m) => messageRowKey(m));
        const fromIdx = keys.indexOf(fromKey);
        const toIdx = keys.indexOf(toKey);
        if (fromIdx < 0 || toIdx < 0) return;
        const [start, end] = fromIdx <= toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
        setCheckedKeys(new Set(keys.slice(start, end + 1)));
        return;
      }
      const range = selectUidRange(selectableInboxUids(), fromUid, toUid);
      setCheckedUids(new Set(range));
    },
    [isUnifiedInboxView, selectableInboxRows, selectableInboxUids]
  );

  const toggleCheckUid = useCallback(
    (uid: number, accountId?: number) => {
      if (isUnifiedInboxView) {
        const row =
          findMessageByUid(uid, accountId) ??
          findMessageByUid(uid, resolveRowAccountId(uid, accountId));
        if (row == null) return;
        const key = messageRowKey(row);
        setCheckedKeys((prev) => {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
        return;
      }
      setCheckedUids((prev) => {
        const next = new Set(prev);
        if (next.has(uid)) next.delete(uid);
        else next.add(uid);
        return next;
      });
    },
    [findMessageByUid, isUnifiedInboxView, resolveRowAccountId]
  );

  const selectAllVisible = useCallback(() => {
    if (isUnifiedInboxView) {
      const keys = selectableInboxRows().map((m) => messageRowKey(m));
      if (keys.length === 0) return;
      setCheckedKeys(new Set(keys));
      selectionAnchorRef.current = selectableInboxRows()[0]?.uid ?? null;
      return;
    }
    const uids = selectableInboxUids();
    if (uids.length === 0) return;
    setCheckedUids(new Set(uids));
    selectionAnchorRef.current = uids[0] ?? null;
  }, [isUnifiedInboxView, selectableInboxRows, selectableInboxUids]);

  useEffect(() => {
    if (isOutboxView) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "a") return;
      event.preventDefault();
      selectAllVisible();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOutboxView, selectAllVisible]);

  const resolveBulkTargets = useCallback(
    (fallbackUid?: number, fallbackAccountId?: number): BulkMessageTarget[] => {
      if (isUnifiedInboxView && checkedKeys.size > 0) {
        return [...checkedKeys].map(parseBulkMessageKey).filter((target) => target.uid > 0);
      }
      if (checkedUids.size > 0) {
        return [...checkedUids].map((uid) => ({ uid }));
      }
      if (fallbackUid != null && fallbackUid > 0) {
        return [
          {
            uid: fallbackUid,
            accountId:
              fallbackAccountId ??
              (isUnifiedInboxView ? resolveRowAccountId(fallbackUid) : undefined),
          },
        ];
      }
      if (selectedUid != null) {
        return [
          {
            uid: selectedUid,
            accountId: isUnifiedInboxView ? resolveRowAccountId(selectedUid) : undefined,
          },
        ];
      }
      return [];
    },
    [checkedKeys, checkedUids, isUnifiedInboxView, resolveRowAccountId, selectedUid]
  );

  const commitDeleteMessages = useCallback(
    (targets: BulkMessageTarget[]) => {
      if (isOutboxView || targets.length === 0) return;
      if (!isUnifiedInboxView && !account) return;

      const uids = targets.map((target) => target.uid);
      // Compteurs sidebar déjà ajustés au début de l’anim (deleteMessages).

      const resolveTargetAccountId = (target: BulkMessageTarget) =>
        isUnifiedInboxView
          ? (target.accountId ?? resolveRowAccountId(target.uid))
          : account?.id;

      // Libellés avant retrait liste (sinon findMessageByUid rate après removeMessages).
      const stepLabels = targets.map((target) => {
        const accountId = resolveTargetAccountId(target);
        const msg = findMessageByUid(target.uid, accountId);
        return mailActivityMessageStepLabel(msg);
      });

      if (inboxListActive) {
        if (isUnifiedInboxView) {
          inbox.removeMessages(uids, {
            accountResolver: (uid) => {
              const match = targets.find((target) => target.uid === uid);
              if (match != null) {
                return resolveTargetAccountId(match);
              }
              return resolveRowAccountId(uid);
            },
          });
        } else {
          inbox.removeMessages(uids);
        }
      }

      const byAccountFolder = new Map<string, number[]>();
      for (const target of targets) {
        const uid = target.uid;
        const accountId = resolveTargetAccountId(target);
        if (accountId == null) continue;
        const fold = messageImapFolder(uid, accountId);
        const listKey = `${accountId}\0${fold}`;
        const list = byAccountFolder.get(listKey) ?? [];
        list.push(uid);
        byAccountFolder.set(listKey, list);
        // Purge hydratation client (mémoire + IDB + doc) — aligné mail_eml_delete serveur.
        removeMessageBodyCacheEntry(accountId, fold, uid);
        clearMailBodyDocumentCacheForMessage(accountId, fold, uid);
      }
      for (const [listKey, folderUids] of byAccountFolder) {
        const [acc, folder] = listKey.split("\0");
        clearBodiesWarming(Number(acc), folder ?? "INBOX", folderUids);
      }

      const mutationJobId = beginMailMutation(
        targets.length === 1
          ? `Suppression · ${stepLabels[0]}`
          : `Suppression · ${targets.length} messages`,
        {
          total: targets.length,
          // Une seule tâche : le titre suffit (évite doublon + barre 1/1).
          stepLabels: targets.length > 1 ? stepLabels : undefined,
        }
      );

      const stepIndexByKey = new Map<string, number>();
      targets.forEach((target, index) => {
        const accountId = resolveTargetAccountId(target);
        if (accountId == null) return;
        stepIndexByKey.set(`${accountId}:${target.uid}`, index);
      });

      const markSteps = (
        accountId: number,
        uids: number[],
        status: "running" | "ok" | "error",
        error?: string
      ) => {
        for (const uid of uids) {
          const idx = stepIndexByKey.get(`${accountId}:${uid}`);
          if (idx != null) setMailMutationStepStatus(mutationJobId, idx, status, error);
        }
      };

      for (const [listKey, folderUids] of byAccountFolder) {
        const [acc, folder] = listKey.split("\0");
        holdMutationUids(Number(acc), folder ?? "INBOX", folderUids);
      }

      const releaseAllDeleteHolds = () => {
        for (const [listKey, folderUids] of byAccountFolder) {
          const [acc, folder] = listKey.split("\0");
          const accountId = Number(acc);
          const fold = folder ?? "INBOX";
          releaseMutationUids(accountId, fold, folderUids);
        }
      };

      const releaseDeleteHoldsKeepTombstones = () => {
        for (const [listKey, folderUids] of byAccountFolder) {
          const [acc, folder] = listKey.split("\0");
          releaseMutationHoldsOnly(Number(acc), folder ?? "INBOX", folderUids);
        }
      };

      void enqueueMailMutation(async () => {
        let processed = 0;
        const foldersByAccount = new Map<number, Set<string>>();
        const trashByAccount = new Map<number, Set<string>>();

        try {
          for (const [listKey, folderUids] of byAccountFolder) {
            const [acc, folder] = listKey.split("\0");
            const accountId = Number(acc);
            const fold = folder ?? "INBOX";
            const folderSet = foldersByAccount.get(accountId) ?? new Set<string>();
            folderSet.add(fold);
            foldersByAccount.set(accountId, folderSet);

            for (let i = 0; i < folderUids.length; i += MAIL_MUTATION_DELETE_CHUNK) {
              const chunk = folderUids.slice(i, i + MAIL_MUTATION_DELETE_CHUNK);
              markSteps(accountId, chunk, "running");
              const res = await deleteMailMessageResilient({
                accountId,
                folder: fold,
                uids: chunk,
                trashFolder: resolveTrashFolder?.(accountId) ?? undefined,
              });

              if (!res.ok) {
                if (isMutationTimeout(res)) {
                  setMailMutationPhase(mutationJobId, "imap-sync");
                  showMailToast(
                    "Suppression envoyée — finalisation en arrière-plan.",
                    "info"
                  );
                  window.setTimeout(() => {
                    releaseDeleteHoldsKeepTombstones();
                    for (const [accountId, folderSet] of foldersByAccount) {
                      const trash = trashByAccount.get(accountId);
                      const folders = [...folderSet, ...(trash ? [...trash] : [])];
                      const viewingFolder =
                        inboxListActive &&
                        (isUnifiedInboxView || folderSet.has(selectedFolder));
                      invalidateFolders({
                        accountId,
                        folders,
                        preserveSnapshots: [],
                        prefetchFolders: [...(trash ?? [])],
                        prefetchRefresh: true,
                        reloadListIfViewing: Boolean(isDraftsFolder && viewingFolder),
                        forceList: isDraftsFolder,
                        mutationJobId,
                      });
                    }
                  }, 5_000);
                  return;
                }
                releaseMutationUids(accountId, fold, chunk);
                markSteps(accountId, chunk, "error", res.message);
                failMailMutation(mutationJobId, res.message);
                showMailToast(res.message, "error");
                recoverAfterMutationError();
                return;
              }

              processed += chunk.length;
              markSteps(accountId, chunk, "ok");
              setMailMutationProgress(mutationJobId, processed, targets.length);
              setMailMutationPhase(mutationJobId, "server-ok");
              releaseMutationHoldsOnly(accountId, fold, chunk);

              if (res.data.trashFolder) {
                const trashSet = trashByAccount.get(accountId) ?? new Set<string>();
                trashSet.add(res.data.trashFolder);
                trashByAccount.set(accountId, trashSet);
              }
            }
          }

          for (const [accountId, folderSet] of foldersByAccount) {
            const trash = trashByAccount.get(accountId);
            const folders = [...folderSet, ...(trash ? [...trash] : [])];
            const viewingFolder =
              inboxListActive &&
              (isUnifiedInboxView || folderSet.has(selectedFolder));
            invalidateFolders({
              accountId,
              folders,
              preserveSnapshots: [],
              prefetchFolders: [...(trash ?? [])],
              prefetchRefresh: true,
              reloadListIfViewing: Boolean(isDraftsFolder && viewingFolder),
              // Brouillons : confirmer l’absence côté IMAP (évite fantôme au prochain sync).
              forceList: isDraftsFolder,
              mutationJobId,
            });
          }
        } catch (err) {
          releaseAllDeleteHolds();
          failMailMutation(
            mutationJobId,
            err instanceof Error ? err.message : "Suppression impossible."
          );
          showMailToast(
            err instanceof Error ? err.message : "Suppression impossible.",
            "error"
          );
          recoverAfterMutationError();
        }
      });
    },
    [
      account,
      findMessageByUid,
      inbox,
      inboxListActive,
      invalidateFolders,
      isDraftsFolder,
      isOutboxView,
      isUnifiedInboxView,
      messageImapFolder,
      recoverAfterMutationError,
      resolveRowAccountId,
      resolveTrashFolder,
      selectedFolder,
    ]
  );

  const deleteMessages = useCallback(
    (targets: BulkMessageTarget[]) => {
      if ((!account && !isUnifiedInboxView) || isOutboxView || targets.length === 0) return;

      const fresh = targets.filter((target) => !exitingUidsRef.current.has(target.uid));
      if (fresh.length === 0) return;

      const pendingMarkKey = markReadOnOpenKeyRef.current;
      if (
        pendingMarkKey != null &&
        fresh.some((target) => messageSelectionKey(target) === pendingMarkKey)
      ) {
        cancelPendingMarkReadOnOpen();
      }

      for (const target of fresh) {
        exitingUidsRef.current.add(target.uid);
      }
      setExitingUids(new Set(exitingUidsRef.current));

      /** Sidebar tout de suite (pas après l’anim de sortie). */
      const resolveTargetAccountId = (target: BulkMessageTarget) =>
        isUnifiedInboxView
          ? (target.accountId ?? resolveRowAccountId(target.uid))
          : account?.id;
      const unreadByFolder = new Map<string, number>();
      const unreadByAccountFolder = new Map<string, number>();
      for (const target of fresh) {
        const accountId = resolveTargetAccountId(target);
        const msg = findMessageByUid(target.uid, accountId);
        const fold = messageImapFolder(target.uid, accountId);
        if (msg && !msg.seen) {
          unreadByFolder.set(fold, (unreadByFolder.get(fold) ?? 0) - 1);
          if (isUnifiedInboxView && accountId != null) {
            const key = `${accountId}\0${fold}`;
            unreadByAccountFolder.set(key, (unreadByAccountFolder.get(key) ?? 0) - 1);
          }
        }
        if (accountId != null && mailSnooze.isSnoozed(fold, target.uid)) {
          void mailSnooze.unsnooze(fold, target.uid);
        }
      }
      if (isUnifiedInboxView && adjustAccountFolderUnread) {
        for (const [key, delta] of unreadByAccountFolder) {
          if (delta === 0) continue;
          const [acc, fold] = key.split("\0");
          adjustAccountFolderUnread(Number(acc), fold ?? "INBOX", delta);
        }
      } else if (!isUnifiedInboxView) {
        for (const [fold, delta] of unreadByFolder) {
          if (delta !== 0) adjustFolderUnread(fold, delta);
        }
      }

      // Snapshot + tombstone tout de suite (pas après l’anim 320 ms).
      // Sinon merge pastille = encore les 5 non-lus → 0 puis 5 puis 0.
      const snapshotByAccountFolder = new Map<string, number[]>();
      const unseenByAccountFolder = new Map<string, number[]>();
      for (const target of fresh) {
        const accountId = resolveTargetAccountId(target);
        if (accountId == null) continue;
        const fold = messageImapFolder(target.uid, accountId);
        const key = `${accountId}\0${fold}`;
        const list = snapshotByAccountFolder.get(key) ?? [];
        list.push(target.uid);
        snapshotByAccountFolder.set(key, list);
        const msg = findMessageByUid(target.uid, accountId);
        if (msg && !msg.seen) {
          const unseen = unseenByAccountFolder.get(key) ?? [];
          unseen.push(target.uid);
          unseenByAccountFolder.set(key, unseen);
        }
      }
      for (const [key, uids] of snapshotByAccountFolder) {
        const [acc, fold] = key.split("\0");
        pinInboxRowExitUids(Number(acc), fold ?? "INBOX", uids);
        tombstoneUids(
          Number(acc),
          fold ?? "INBOX",
          uids,
          undefined,
          new Set(unseenByAccountFolder.get(key) ?? [])
        );
        patchFolderSnapshotRemoveUids(Number(acc), fold ?? "INBOX", uids);
      }

      const freshUids = fresh.map((target) => target.uid);
      if (selectedUid != null && freshUids.includes(selectedUid)) {
        const anchorAccountId = resolveRowAccountId(selectedUid);
        const anchorMsg =
          findMessageByUid(selectedUid, anchorAccountId) ?? ({ uid: selectedUid, accountId: anchorAccountId } as MailMessageSummary);
        const removedKeys = new Set(
          fresh.map((target) => {
            const accountId = target.accountId ?? resolveRowAccountId(target.uid);
            const msg = findMessageByUid(target.uid, accountId);
            return messageSelectionKey(msg ?? { uid: target.uid, accountId });
          })
        );
        const next = advanceSelectionAfterDelete
          ? pickNextMessageAfterRemove(displayMessages, removedKeys, anchorMsg)
          : null;
        if (next) {
          setSelectedUid(next.uid);
          if (isUnifiedInboxView && next.accountId != null) {
            setSelectedReaderAccountId?.(next.accountId);
          } else {
            setSelectedReaderAccountId?.(null);
          }
        } else {
          setSelectedUid(null);
          setSelectedReaderAccountId?.(null);
        }
      }
      setCheckedUids(new Set());
      setCheckedKeys(new Set());

      window.setTimeout(() => {
        for (const [key, uids] of snapshotByAccountFolder) {
          const [acc, fold] = key.split("\0");
          unpinInboxRowExitUids(Number(acc), fold ?? "INBOX", uids);
        }
        for (const target of fresh) {
          exitingUidsRef.current.delete(target.uid);
        }
        setExitingUids(new Set(exitingUidsRef.current));
        commitDeleteMessages(fresh);
      }, INBOX_ROW_EXIT_MS);
    },
    [
      account,
      adjustAccountFolderUnread,
      adjustFolderUnread,
      advanceSelectionAfterDelete,
      commitDeleteMessages,
      displayMessages,
      findMessageByUid,
      isOutboxView,
      isUnifiedInboxView,
      mailSnooze,
      messageImapFolder,
      resolveRowAccountId,
      selectedUid,
      setSelectedReaderAccountId,
      setSelectedUid,
      cancelPendingMarkReadOnOpen,
    ]
  );

  /** Desktop : Suppr → même chemin que la barre d’actions (sélection cochée ou mail ouvert). */
  useEffect(() => {
    if (isOutboxView) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      if (event.key !== "Delete") return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const targets = resolveBulkTargets();
      if (targets.length === 0) return;
      event.preventDefault();
      deleteMessages(targets);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleteMessages, isOutboxView, resolveBulkTargets]);

  const ensureMessageSeen = useCallback(
    (uid: number, accountId?: number) => {
      cancelPendingMarkReadOnOpen();
      const msg = findMessageByUid(uid, accountId);
      if (msg?.seen) return;

      const pendingKey = messageSelectionKey({ uid, accountId });
      markReadOnOpenKeyRef.current = pendingKey;
      const markRead = () => {
        markReadOnOpenTimerRef.current = null;
        if (markReadOnOpenKeyRef.current !== pendingKey) return;
        markReadOnOpenKeyRef.current = null;
        applySeenToUids([uid], true, "click");
      };
      if (markReadOnOpenDelayMs <= 0) {
        markRead();
        return;
      }
      markReadOnOpenTimerRef.current = window.setTimeout(markRead, markReadOnOpenDelayMs);
    },
    [applySeenToUids, cancelPendingMarkReadOnOpen, findMessageByUid, markReadOnOpenDelayMs]
  );

  const resolveAttachFolder = useCallback(
    (uid: number, accountId: number) => messageImapFolder(uid, accountId),
    [messageImapFolder]
  );

  const allKnownMessages = useMemo(() => {
    const map = new Map<string, MailMessageSummary>();
    // inbox brut d'abord — display / labeled (flags PJ enrichis) écrasent en dernier.
    for (const msg of [...inboxMessages, ...labeledMessages, ...displayMessages]) {
      map.set(messageRowKey(msg), msg);
    }
    return [...map.values()];
  }, [displayMessages, inboxMessages, labeledMessages]);

  const resolveAttachmentTargets = useCallback(
    (uids: number[]) => {
      const keys = uids.map((uid) => ({
        uid,
        accountId: resolveRowAccountId(uid, contextMenu?.accountId),
      }));
      return filterMessagesWithAttachments(
        allKnownMessages,
        keys,
        account?.id,
        resolveAttachFolder
      );
    },
    [
      account?.id,
      allKnownMessages,
      contextMenu?.accountId,
      resolveAttachFolder,
      resolveRowAccountId,
    ]
  );

  const selectOnlyWithAttachments = useCallback(() => {
    const rows = selectableInboxRows().filter((m) => m.hasAttachments === true);
    if (isUnifiedInboxView) {
      setCheckedKeys(new Set(rows.map((m) => messageRowKey(m))));
      setCheckedUids(new Set(rows.map((m) => m.uid)));
    } else {
      setCheckedUids(new Set(rows.map((m) => m.uid)));
      setCheckedKeys(new Set());
    }
    selectionAnchorRef.current = rows[0]?.uid ?? null;
  }, [isUnifiedInboxView, selectableInboxRows]);

  const closeAttachPicker = useCallback(() => {
    setAttachPicker(null);
  }, []);

  const closeEmlArchive = useCallback(() => {
    setEmlArchive(null);
  }, []);

  const openArchiveToCoffre = useCallback(
    (targets: BulkMessageTarget[]) => {
      const resolved: MailEmlArchiveTarget[] = [];
      for (const target of targets) {
        if (target.uid <= 0) continue;
        const accountId = target.accountId ?? resolveRowAccountId(target.uid) ?? account?.id;
        if (accountId == null) continue;
        const msg = findMessageByUid(target.uid, accountId);
        resolved.push({
          accountId,
          folder: messageImapFolder(target.uid, accountId),
          uid: target.uid,
          subject: msg?.subject?.trim() || "",
          date: msg?.date,
        });
      }
      if (resolved.length === 0) {
        showMailToast("Aucun message à archiver.", "error");
        return;
      }
      prefetchCoffrePickerList("");
      setEmlArchive({ targets: resolved });
    },
    [account?.id, findMessageByUid, messageImapFolder, resolveRowAccountId]
  );

  const confirmBulkCoffre = useCallback(
    async (destPath: string, zone: CoffreListZone) => {
      if (!attachPicker || attachPicker.mode !== "coffre") return;
      setAttachBusy(true);
      setAttachProgress({ done: 0, total: 1 });
      try {
        const result = await bulkSaveAttachmentsToCoffre(
          attachPicker.targets,
          destPath,
          zone,
          resolveAttachFolder,
          setAttachProgress
        );
        setAttachPicker(null);
        if (result.ok === 0) {
          showMailToast(
            result.skipped > 0 || result.failed > 0
              ? "Aucune pièce jointe trouvée sur les messages sélectionnés."
              : "Aucune pièce jointe enregistrée.",
            "error"
          );
        } else {
          showMailToast(
            `${result.ok} pièce(s) jointe(s) enregistrée(s) dans le Coffre.${result.failed ? ` ${result.failed} échec(s).` : ""}`,
            "success"
          );
        }
      } finally {
        setAttachBusy(false);
        setAttachProgress(null);
      }
    },
    [attachPicker, resolveAttachFolder]
  );

  const confirmArchiveToCoffre = useCallback(
    async (destPath: string, zone: CoffreListZone) => {
      if (!emlArchive) return;
      setAttachBusy(true);
      setAttachProgress({ done: 0, total: emlArchive.targets.length });
      try {
        const result = await archiveMailMessagesToCoffre(
          emlArchive.targets,
          destPath,
          zone,
          setAttachProgress
        );
        setEmlArchive(null);
        if (result.ok === 0) {
          showMailToast(result.errors[0] ?? "Archivage impossible.", "error");
          return;
        }
        const failedNote = result.failed > 0 ? ` ${result.failed} échec(s).` : "";
        showMailToast(
          result.ok === 1
            ? `Message archivé dans le Coffre.${failedNote}`
            : `${result.ok} messages archivés dans le Coffre.${failedNote}`,
          result.failed > 0 ? "error" : "success"
        );
      } finally {
        setAttachBusy(false);
        setAttachProgress(null);
      }
    },
    [emlArchive]
  );

  const runAttachmentBulkAction = useCallback(
    async (actionId: "downloadAttachments" | "saveToCoffre", uids: number[]) => {
      const targets = resolveAttachmentTargets(uids);
      if (targets.length === 0) {
        showMailToast("Aucun message sélectionné valide.", "error");
        return;
      }

      if (actionId === "saveToCoffre") {
        prefetchCoffrePickerList("");
        setAttachPicker({ mode: "coffre", targets });
        return;
      }
      setAttachBusy(true);
      setAttachProgress({ done: 0, total: 1 });
      try {
        const result = await bulkDownloadAttachments(targets, resolveAttachFolder, setAttachProgress);
        if (result.ok === 0) {
          showMailToast(
            result.skipped > 0
              ? "Aucune pièce jointe trouvée sur les messages sélectionnés."
              : "Aucune pièce jointe téléchargée.",
            "error"
          );
        } else {
          showMailToast(
            `${result.ok} pièce(s) jointe(s) téléchargée(s).${result.failed ? ` ${result.failed} échec(s).` : ""}`,
            "success"
          );
        }
      } finally {
        setAttachBusy(false);
        setAttachProgress(null);
      }
    },
    [resolveAttachmentTargets, resolveAttachFolder]
  );

  const runMessageAction = useCallback(
    async (actionId: string, targets: BulkMessageTarget[]) => {
      const uids = targets.map((target) => target.uid);
      if ((!account && !isUnifiedInboxView) || isOutboxView || uids.length === 0) return;

      if (actionId === "selectWithAttachments") {
        selectOnlyWithAttachments();
        return;
      }

      if (
        actionId === "downloadAttachments" ||
        actionId === "saveToCoffre"
      ) {
        await runAttachmentBulkAction(actionId, uids);
        return;
      }

      if (actionId === "archiveToCoffre") {
        openArchiveToCoffre(targets);
        return;
      }

      if (actionId === "redownloadEml") {
        if (isUnifiedInboxView || uids.length !== 1 || !account) return;
        const uid = uids[0];
        const fold = messageImapFolder(uid);
        const accountId = account.id;
        markBodiesWarming(accountId, fold, [uid]);
        removeMessageBodyCacheEntry(accountId, fold, uid);
        clearMailBodyDocumentCacheForMessage(accountId, fold, uid);
        showMailToast("Retéléchargement depuis IMAP…", "info");
        try {
          const res = await fetchMailEmlBatch({
            accountId,
            folder: fold,
            uids: [uid],
            force: true,
          });
          if (!res.ok) {
            showMailToast(res.message || "Échec du retéléchargement.", "error");
            return;
          }
          const items = res.data.items ?? [];
          const failed = items.find((item) => item.error);
          if (failed?.error) {
            showMailToast(failed.error, "error");
            return;
          }
          await storeFetchedEmlItems(accountId, fold, items);
          if (selectedUid === uid) {
            await fetchMessageBodyWithCache(accountId, fold, uid, {
              force: true,
              interactive: true,
            });
          }
          showMailToast("Message retéléchargé depuis IMAP.", "success");
        } catch (err) {
          const message = err instanceof Error ? err.message : "Échec du retéléchargement.";
          showMailToast(message, "error");
        } finally {
          clearBodiesWarming(accountId, fold, [uid]);
        }
        return;
      }

      if (actionId === "delete") {
        deleteMessages(targets);
        return;
      }

      if (actionId === "move") {
        if (isUnifiedInboxView) return;
        pendingMoveUidsRef.current = uids;
        setPendingActionCount(uids.length);
        setMoveDialogOpen(true);
        return;
      }

      if (actionId === "snooze:pick") {
        if (isUnifiedInboxView || uids.length !== 1 || isSnoozedView) return;
        if (snoozeBusyRef.current) {
          showMailToast("Mise en attente en cours — patientez un instant.", "info");
          return;
        }
        const gmailLocal = isGmailMailAccount(account);
        const holdFolder = resolveSnoozeHoldFolder?.() ?? null;
        if (!gmailLocal && !holdFolder) {
          onSnoozeFolderRequired?.();
          showMailToast(
            "Créez ou branchez un dossier IMAP « En attente » (Organisation des dossiers).",
            "info",
            6500
          );
          return;
        }
        const uid = uids[0];
        const msg = findMessageByUid(uid);
        setSnoozeDialog({ uid, subject: msg?.subject });
        return;
      }

      if (actionId === "markRead") {
        applySeenToUids(uids, true, "explicit");
        return;
      }

      if (actionId === "markUnread") {
        applySeenToUids(uids, false, "explicit");
        return;
      }

      if (actionId === "toggleImportant") {
        const targets = uids.map(
          (uid) =>
            labeledMessages.find((m) => m.uid === uid) ?? inboxMessages.find((m) => m.uid === uid)
        );
        const markFlagged = !targets.every((m) => m?.flagged);
        for (const uid of uids) {
          inbox.markMessageFlagged(uid, markFlagged);
        }
        return;
      }

      if (actionId === "toggleFavorite") {
        const markFavorite = !uids.every((uid) => {
          const row =
            labeledMessages.find((m) => m.uid === uid) ?? inboxMessages.find((m) => m.uid === uid);
          const label = messageLabels.labels.get(uid);
          return label?.favorite ?? row?.favorite ?? false;
        });
        for (const uid of uids) {
          void messageLabels.updateLabel(uid, { favorite: markFavorite });
        }
        return;
      }

      if (actionId.startsWith("color:")) {
        const color: MailColorLabel | null =
          actionId === "color:none" ? null : (actionId.slice(6) as MailColorLabel);
        for (const uid of uids) {
          void messageLabels.updateLabel(uid, { color });
        }
        return;
      }

      if (actionId === "snooze:cancel") {
        if (uids.length !== 1) return;
        const uid = uids[0];
        const entry = mailSnooze.snoozes.find((s) => s.uid === uid);
        const folder = entry?.folder ?? selectedFolder;
        void mailSnooze.unsnooze(folder, uid);
        if (selectedUid === uid) setSelectedUid(null);
        clearSelection();
        return;
      }

      if (uids.length !== 1) return;
      const uid = uids[0];

      if (actionId === "reply") {
        setSelectedUid(null);
        await openComposeForUid(uid, "reply");
        return;
      }
      if (actionId === "replyAll") {
        setSelectedUid(null);
        await openComposeForUid(uid, "replyAll");
        return;
      }
      if (actionId === "forward") {
        setSelectedUid(null);
        await openComposeForUid(uid, "forward");
      }
    },
    [
      account,
      applySeenToUids,
      clearSelection,
      deleteMessages,
      findMessageByUid,
      inbox,
      inboxMessages,
      isOutboxView,
      isSnoozedView,
      isUnifiedInboxView,
      labeledMessages,
      mailSnooze,
      messageImapFolder,
      messageLabels,
      openComposeForUid,
      openArchiveToCoffre,
      runAttachmentBulkAction,
      selectOnlyWithAttachments,
      selectedFolder,
      selectedUid,
      setSelectedUid,
      resolveSnoozeHoldFolder,
      onSnoozeFolderRequired,
    ]
  );

  const handleContextMenuAction = useCallback(
    (actionId: string) => {
      const targets = resolveBulkTargets(contextMenu?.uid, contextMenu?.accountId);
      void runMessageAction(actionId, targets);
      setContextMenu(null);
    },
    [contextMenu, resolveBulkTargets, runMessageAction]
  );

  const handleBulkAction = useCallback(
    (actionId: string) => {
      void runMessageAction(actionId, resolveBulkTargets());
    },
    [resolveBulkTargets, runMessageAction]
  );

  const handleMessageAction = useCallback(
    async (actionId: string) => {
      if ((!account && !isUnifiedInboxView) || isOutboxView) return;
      const targets = resolveBulkTargets(selectedUid ?? undefined);
      if (targets.length === 0) return;
      await runMessageAction(actionId, targets);
    },
    [account, isOutboxView, isUnifiedInboxView, resolveBulkTargets, runMessageAction, selectedUid]
  );

  const handleToggleCheck = useCallback(
    (uid: number, event?: React.MouseEvent, accountId?: number) => {
      if (isOutboxView) return;

      if (isRangeSelectClick(event)) {
        event?.preventDefault();
        const anchor = selectionAnchorRef.current ?? selectedUid ?? uid;
        applyRangeSelection(anchor, uid, accountId);
        selectionAnchorRef.current = anchor;
        setSelectedUid(uid);
        if (isUnifiedInboxView) {
          setSelectedReaderAccountId?.(resolveRowAccountId(uid, accountId) ?? null);
        }
        return;
      }

      toggleCheckUid(uid, accountId);
      selectionAnchorRef.current = uid;
      if (isMultiSelectClick(event)) {
        setSelectedUid(uid);
        if (isUnifiedInboxView) {
          setSelectedReaderAccountId?.(resolveRowAccountId(uid, accountId) ?? null);
        }
      }
    },
    [
      applyRangeSelection,
      isOutboxView,
      isUnifiedInboxView,
      resolveRowAccountId,
      selectedUid,
      setSelectedReaderAccountId,
      setSelectedUid,
      toggleCheckUid,
    ]
  );

  const handleRowContextMenu = useCallback(
    (event: React.MouseEvent, uid: number, accountId?: number) => {
      if (isOutboxView) return;
      event.preventDefault();
      setContextMenu({ x: event.clientX, y: event.clientY, uid, accountId });
    },
    [isOutboxView]
  );

  const handleSelectMessage = useCallback(
    (
      uid: number,
      event?: React.MouseEvent,
      options?: { bodyBlocked?: boolean; accountId?: number }
    ) => {
      if (isOutboxView) {
        setSelectedUid(uid);
        return;
      }

      const accountId = options?.accountId;

      if (isRangeSelectClick(event)) {
        event?.preventDefault();
        const anchor = selectionAnchorRef.current ?? selectedUid ?? uid;
        applyRangeSelection(anchor, uid, accountId);
        selectionAnchorRef.current = anchor;
        setSelectedUid(uid);
        if (isUnifiedInboxView) {
          setSelectedReaderAccountId?.(resolveRowAccountId(uid, accountId) ?? null);
        }
        return;
      }

      if (isMultiSelectClick(event)) {
        event?.preventDefault();
        toggleCheckUid(uid, accountId);
        selectionAnchorRef.current = uid;
        setSelectedUid(uid);
        if (isUnifiedInboxView) {
          setSelectedReaderAccountId?.(resolveRowAccountId(uid, accountId) ?? null);
        }
        return;
      }

      if (isDraftsFolder) {
        void openDraftForEdit(uid);
        return;
      }

      if (options?.bodyBlocked) {
        return;
      }

      ensureMessageSeen(uid, accountId);

      selectionAnchorRef.current = uid;
      setCheckedUids(new Set());
      setCheckedKeys(new Set());
      setSelectedUid(uid);
      if (isUnifiedInboxView) {
        setSelectedReaderAccountId?.(resolveRowAccountId(uid, accountId) ?? null);
      }
    },
    [
      applyRangeSelection,
      ensureMessageSeen,
      isDraftsFolder,
      isOutboxView,
      isUnifiedInboxView,
      openDraftForEdit,
      resolveRowAccountId,
      selectedUid,
      setSelectedReaderAccountId,
      setSelectedUid,
      toggleCheckUid,
    ]
  );

  const confirmMoveMessages = useCallback(
    async (targetFolder: string) => {
      if (!account || isOutboxView) return;

      const uids = pendingMoveUidsRef.current;
      if (uids.length === 0) return;

      setMoveDialogOpen(false);
      pendingMoveUidsRef.current = [];

      const unreadByFolder = new Map<string, number>();
      for (const uid of uids) {
        const msg = findMessageByUid(uid);
        if (msg && !msg.seen) {
          const fold = messageImapFolder(uid);
          unreadByFolder.set(fold, (unreadByFolder.get(fold) ?? 0) - 1);
        }
      }
      for (const [fold, delta] of unreadByFolder) {
        if (delta !== 0) adjustFolderUnread(fold, delta);
      }
      if (inboxListActive) {
        if (isUnifiedInboxView) {
          inbox.removeMessages(uids, { accountResolver: resolveRowAccountId });
        } else {
          inbox.removeMessages(uids);
        }
      }
      if (uids.includes(selectedUid ?? -1)) {
        setSelectedUid(null);
      }
      setCheckedUids(new Set());

      const bySourceFolder = new Map<string, number[]>();
      for (const uid of uids) {
        const fold = messageImapFolder(uid);
        const list = bySourceFolder.get(fold) ?? [];
        list.push(uid);
        bySourceFolder.set(fold, list);
      }

      const stepLabels = uids.map((uid) => {
        const msg = findMessageByUid(uid);
        return mailActivityMessageStepLabel(msg);
      });

      const mutationJobId = beginMailMutation(
        uids.length === 1
          ? `Déplacement · ${stepLabels[0]}`
          : `Déplacement · ${uids.length} messages`,
        {
          total: uids.length,
          stepLabels: uids.length > 1 ? stepLabels : undefined,
        }
      );

      const stepIndexByUid = new Map(uids.map((uid, index) => [uid, index]));
      const markMoveSteps = (
        folderUids: number[],
        status: "running" | "ok" | "error",
        error?: string
      ) => {
        for (const uid of folderUids) {
          const idx = stepIndexByUid.get(uid);
          if (idx != null) setMailMutationStepStatus(mutationJobId, idx, status, error);
        }
      };

      for (const [sourceFolder, folderUids] of bySourceFolder) {
        holdMutationUids(account.id, sourceFolder, folderUids);
      }

      const releaseAllMoveHolds = () => {
        for (const [sourceFolder, folderUids] of bySourceFolder) {
          releaseMutationUids(account.id, sourceFolder, folderUids);
        }
      };

      const releaseMoveHoldsKeepTombstones = () => {
        for (const [sourceFolder, folderUids] of bySourceFolder) {
          releaseMutationHoldsOnly(account.id, sourceFolder, folderUids);
        }
      };

      void enqueueMailMutation(async () => {
        let processed = 0;
        try {
          for (const [sourceFolder, folderUids] of bySourceFolder) {
            markMoveSteps(folderUids, "running");
            const res = await moveMailMessageResilient({
              accountId: account.id,
              folder: sourceFolder,
              uids: folderUids,
              targetFolder,
            });

            if (!res.ok) {
              if (isMutationTimeout(res)) {
                setMailMutationPhase(mutationJobId, "imap-sync");
                showMailToast(
                  "Déplacement envoyé — finalisation en arrière-plan.",
                  "info"
                );
                window.setTimeout(() => {
                  releaseMoveHoldsKeepTombstones();
                  const sourceFolders = [...bySourceFolder.keys()];
                  const viewingSource =
                    inboxListActive && !isUnifiedInboxView && sourceFolders.includes(selectedFolder);
                  invalidateFolders({
                    accountId: account.id,
                    folders: [...sourceFolders, targetFolder],
                    preserveSnapshots: viewingSource ? [selectedFolder] : [],
                    prefetchFolders: [targetFolder],
                    prefetchRefresh: true,
                    reloadListIfViewing: inboxListActive && selectedFolder === targetFolder,
                    forceList: false,
                    mutationJobId,
                  });
                }, 5_000);
                return;
              }
              releaseAllMoveHolds();
              markMoveSteps(folderUids, "error", res.message);
              failMailMutation(mutationJobId, res.message);
              showMailToast(res.message, "error");
              recoverAfterMutationError();
              return;
            }
            markMoveSteps(folderUids, "ok");
            processed += folderUids.length;
            setMailMutationProgress(mutationJobId, processed, uids.length);
            setMailMutationPhase(mutationJobId, "server-ok");
            releaseMutationHoldsOnly(account.id, sourceFolder, folderUids);
            await relocateStoredMessages(account.id, folderUids, sourceFolder, targetFolder);
          }

          setMailMutationPhase(mutationJobId, "server-ok");
          setMailMutationProgress(mutationJobId, uids.length, uids.length);

          const sourceFolders = [...bySourceFolder.keys()];
          const viewingSource =
            inboxListActive && !isUnifiedInboxView && sourceFolders.includes(selectedFolder);

          invalidateFolders({
            accountId: account.id,
            folders: [...sourceFolders, targetFolder],
            preserveSnapshots: viewingSource ? [selectedFolder] : [],
            prefetchFolders: [targetFolder],
            prefetchRefresh: true,
            reloadListIfViewing: inboxListActive && selectedFolder === targetFolder,
            forceList: false,
            mutationJobId,
          });
        } catch (err) {
          releaseAllMoveHolds();
          failMailMutation(
            mutationJobId,
            err instanceof Error ? err.message : "Déplacement impossible."
          );
          showMailToast(
            err instanceof Error ? err.message : "Déplacement impossible.",
            "error"
          );
          recoverAfterMutationError();
        }
      });
    },
    [
      account,
      adjustFolderUnread,
      findMessageByUid,
      inbox,
      inboxListActive,
      invalidateFolders,
      isOutboxView,
      isUnifiedInboxView,
      messageImapFolder,
      recoverAfterMutationError,
      selectedFolder,
      selectedUid,
      setSelectedUid,
    ]
  );

  const closeMoveDialog = useCallback(() => {
    setMoveDialogOpen(false);
    pendingMoveUidsRef.current = [];
  }, []);

  const closeSnoozeDialog = useCallback(() => {
    setSnoozeDialog(null);
  }, []);

  const confirmSnooze = useCallback(
    (untilIso: string) => {
      const pending = snoozeDialog;
      if (!pending || isSnoozedView || snoozeBusyRef.current) return;

      const gmailLocal = isGmailMailAccount(account);
      const holdFolder = resolveSnoozeHoldFolder?.() ?? null;
      if (!gmailLocal && !holdFolder) {
        setSnoozeDialog(null);
        onSnoozeFolderRequired?.();
        showMailToast(
          "Créez ou branchez un dossier IMAP « En attente » (Organisation des dossiers).",
          "info",
          6500
        );
        return;
      }

      const imapFolder = messageImapFolder(pending.uid);
      const { uid, subject } = pending;

      setSnoozeDialog(null);
      if (selectedUid === uid) setSelectedUid(null);
      clearSelection();

      if (inboxListActive && !gmailLocal) {
        inbox.removeMessages([uid]);
      }
      if (account && holdFolder && !gmailLocal) {
        relocateMessageBodyCache(account.id, uid, imapFolder, holdFolder);
      }

      // Gmail local : masquage via isSnoozed seulement — pas de remove/tombstone
      // (sinon le sync IMAP croit à une arrivée → Préparation ×N).
      if (account && !gmailLocal) {
        holdMutationUids(account.id, imapFolder, [uid]);
      }

      const releaseSnoozeSourceHold = () => {
        if (!account || gmailLocal) return;
        releaseMutationUids(account.id, imapFolder, [uid]);
      };

      snoozeBusyRef.current = true;
      setSnoozeBusy(true);

      const runSnooze = async () => {
        try {
          const snoozeResult = await mailSnooze.snooze(imapFolder, uid, untilIso, subject, {
            holdFolder: gmailLocal ? null : holdFolder,
          });
          if (!snoozeResult.ok) {
            releaseSnoozeSourceHold();
            showMailToast(
              snoozeResult.message?.trim() || "Impossible d'enregistrer le report. Réessayez.",
              "error"
            );
            void mailSnooze.reload({ silent: true });
            recoverAfterMutationError();
            return;
          }
          if (account) {
            if (gmailLocal) {
              // Rien à lever — pas de hold/tombstone posé.
            } else {
              releaseMutationHoldsOnly(account.id, imapFolder, [uid]);
            }
          }
          if (account) {
            if (!gmailLocal && holdFolder) {
              void relocateStoredMessages(account.id, [uid], imapFolder, holdFolder);
              invalidateFolders({
                accountId: account.id,
                folders: [imapFolder, holdFolder],
                preserveSnapshots:
                  inboxListActive && selectedFolder === imapFolder ? [imapFolder] : [],
                prefetchFolders: [holdFolder],
                reloadListIfViewing: selectedFolder === holdFolder,
                forceList: false,
              });
            } else {
              // Gmail overlay : pas de MOVE — juste invalider la source.
              invalidateFolders({
                accountId: account.id,
                folders: [imapFolder],
                preserveSnapshots:
                  inboxListActive && selectedFolder === imapFolder ? [imapFolder] : [],
                forceList: false,
              });
            }
          }
        } catch {
          releaseSnoozeSourceHold();
          showMailToast("Impossible d'enregistrer le report. Réessayez.", "error");
          recoverAfterMutationError();
        } finally {
          snoozeBusyRef.current = false;
          setSnoozeBusy(false);
        }
      };

      // Gmail = MySQL only — hors file IMAP (évite blocage + faux « nouveau mail »).
      if (gmailLocal) {
        void runSnooze();
      } else {
        void enqueueMailMutation(runSnooze);
      }
    },
    [
      account,
      clearSelection,
      inbox,
      inboxListActive,
      invalidateFolders,
      isSnoozedView,
      mailSnooze,
      messageImapFolder,
      onSnoozeFolderRequired,
      recoverAfterMutationError,
      resolveSnoozeHoldFolder,
      selectedFolder,
      selectedUid,
      setSelectedUid,
      snoozeDialog,
    ]
  );

  const contextTargetUids = useMemo(() => {
    if (isUnifiedInboxView && checkedKeys.size > 0) {
      return [...checkedKeys].map((key) => {
        const sep = key.indexOf(":");
        return sep >= 0 ? Number(key.slice(sep + 1)) : Number(key);
      });
    }
    if (checkedUids.size > 0) return [...checkedUids];
    if (contextMenu?.uid != null) return [contextMenu.uid];
    if (selectedUid != null) return [selectedUid];
    return [];
  }, [checkedKeys, checkedUids, contextMenu, isUnifiedInboxView, selectedUid]);

  const resolveContextTargetMessage = useCallback(
    (uid: number) => {
      const accountId =
        contextMenu?.uid === uid
          ? contextMenu.accountId
          : isUnifiedInboxView
            ? resolveRowAccountId(uid)
            : undefined;
      return (
        findMessageByUid(uid, accountId) ??
        displayMessages.find((m) => m.uid === uid) ??
        labeledMessages.find((m) => m.uid === uid)
      );
    },
    [contextMenu, displayMessages, findMessageByUid, isUnifiedInboxView, labeledMessages, resolveRowAccountId]
  );

  const isRowSelected = useCallback(
    (msg: MailMessageSummary) =>
      selectedUid === msg.uid &&
      (!isUnifiedInboxView ||
        selectedReaderAccountId == null ||
        msg.accountId == null ||
        msg.accountId === selectedReaderAccountId),
    [isUnifiedInboxView, selectedReaderAccountId, selectedUid]
  );

  const isRowChecked = useCallback(
    (msg: MailMessageSummary) =>
      isUnifiedInboxView ? checkedKeys.has(messageRowKey(msg)) : checkedUids.has(msg.uid),
    [checkedKeys, checkedUids, isUnifiedInboxView]
  );

  const isContextMenuRow = useCallback(
    (msg: MailMessageSummary) =>
      contextMenu != null &&
      contextMenu.uid === msg.uid &&
      (contextMenu.accountId == null ||
        msg.accountId == null ||
        contextMenu.accountId === msg.accountId),
    [contextMenu]
  );

  const selectionCount = isUnifiedInboxView ? checkedKeys.size : checkedUids.size;

  const contextMenuItems = useMemo(() => {
    const targets = contextTargetUids
      .map((uid) => resolveContextTargetMessage(uid))
      .filter((m): m is NonNullable<typeof m> => m != null);
    const allFlagged = targets.length > 0 && targets.every((m) => m.flagged);
    const allFavorite = targets.length > 0 && targets.every((m) => m.favorite);
    const sharedColor =
      targets.length > 0 && targets.every((m) => m.colorLabel === targets[0]?.colorLabel)
        ? (targets[0]?.colorLabel ?? null)
        : null;

    return buildMessageContextMenuItems({
      compose: !isOutboxView && !isUnifiedInboxView,
      seen: targets[0]?.seen ?? true,
      flagged: allFlagged,
      favorite: allFavorite,
      colorLabel: sharedColor,
      bulk: selectionCount > 0,
      bulkCount: selectionCount > 0 ? selectionCount : contextTargetUids.length,
      snoozed:
        isSnoozedView ||
        (contextTargetUids.length === 1 &&
          mailSnooze.isSnoozed(selectedFolder, contextTargetUids[0])),
      showSnooze: !isDraftsFolder && !isOutboxView && !isUnifiedInboxView,
      showMove: !isUnifiedInboxView,
      showLabels: !isUnifiedInboxView,
      showArchiveToCoffre: !isOutboxView,
      showRedownloadEml: !isUnifiedInboxView && contextTargetUids.length === 1 && !isDraftsFolder,
    });
  }, [
    contextTargetUids,
    isDraftsFolder,
    isOutboxView,
    isSnoozedView,
    isUnifiedInboxView,
    mailSnooze,
    resolveContextTargetMessage,
    selectedFolder,
    selectionCount,
  ]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  return useMemo(
    () => ({
      checkedUids,
      selectionCount,
      exitingUids,
      contextMenu,
      moveDialogOpen,
      snoozeDialog,
      snoozeBusy,
      pendingActionCount,
      contextMenuItems,
      isRowSelected,
      isRowChecked,
      isContextMenuRow,
      clearSelection,
      handleToggleCheck,
      handleRowContextMenu,
      handleContextMenuAction,
      handleBulkAction,
      handleMessageAction,
      handleSelectMessage,
      confirmMoveMessages,
      closeMoveDialog,
      confirmSnooze,
      closeSnoozeDialog,
      closeContextMenu,
      attachPicker,
      attachProgress,
      attachBusy,
      confirmBulkCoffre,
      closeAttachPicker,
      emlArchive,
      confirmArchiveToCoffre,
      closeEmlArchive,
      openArchiveToCoffre,
      selectOnlyWithAttachments,
    }),
    [
      checkedUids,
      selectionCount,
      exitingUids,
      clearSelection,
      closeAttachPicker,
      closeContextMenu,
      closeMoveDialog,
      closeSnoozeDialog,
      attachBusy,
      attachPicker,
      attachProgress,
      emlArchive,
      openArchiveToCoffre,
      confirmArchiveToCoffre,
      confirmBulkCoffre,
      closeEmlArchive,
      confirmMoveMessages,
      confirmSnooze,
      contextMenu,
      contextMenuItems,
      handleBulkAction,
      handleContextMenuAction,
      handleMessageAction,
      handleRowContextMenu,
      handleSelectMessage,
      handleToggleCheck,
      isContextMenuRow,
      isRowChecked,
      isRowSelected,
      moveDialogOpen,
      selectOnlyWithAttachments,
      snoozeBusy,
      snoozeDialog,
      pendingActionCount,
    ]
  );
}
